import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Avatar, Box, Button, Typography } from "@mui/material";

import useMatchNights from "../../match-night/useMatchNights";
import useCountdown from "../../match-night/useCountdown";
import { formatNightDateLabel, formatServersLine, isNightScheduledToday } from "../../match-night/cetTime";
import { MATCH_NIGHT_REGIONS, normalizeMatchNightRegion } from "../../match-night/preferredMatchNight";
import MatchNightRsvpDialog from "../../match-night/MatchNightRsvpDialog";
import { useTemplateTrack } from "../TemplateTrackingProvider";
import { colors } from "../dark-hero/colors";
import { formatMatchNightStatus } from "./matchNightStatus";

const wrapSx = {
  width: "100%",
  maxWidth: { xs: "22rem", md: "40rem" },
  mx: "auto",
  px: { xs: 1.25, md: 2 },
  py: { xs: 1.25, md: 1.5 },
  border: `1px solid ${colors.border.default}`,
  borderRadius: "14px",
  background: "linear-gradient(180deg, rgba(255,255,255,0.03), transparent)",
  textAlign: "left",
};

const kickerSx = {
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: colors.primary.main,
  mb: 0.4,
};

const tabRowSx = {
  display: "inline-flex",
  gap: 0.5,
  mt: 0.35,
  mb: 0.75,
  p: 0.35,
  border: `1px solid ${colors.border.default}`,
  borderRadius: "999px",
  backgroundColor: "rgba(0,0,0,0.22)",
};

function tabSx(active) {
  return {
    border: 0,
    borderRadius: "999px",
    px: 1.2,
    py: 0.45,
    cursor: "pointer",
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: active ? "#0B0D0F" : colors.text.secondary,
    backgroundColor: active ? colors.primary.main : "transparent",
    "&:hover": {
      color: active ? "#0B0D0F" : colors.text.primary,
      backgroundColor: active ? colors.primary.main : "rgba(255,255,255,0.06)",
    },
  };
}

const dateSx = {
  fontSize: { xs: "0.9rem", md: "1rem" },
  fontWeight: 700,
  color: colors.text.primary,
};

const metaSx = {
  fontSize: "0.8rem",
  color: colors.text.secondary,
  mt: 0.25,
};

const countSx = {
  mt: 0.75,
  fontSize: "0.85rem",
  fontWeight: 600,
  color: colors.text.primary,
};

const noteSx = {
  mt: 0.25,
  fontSize: "0.75rem",
  color: colors.text.secondary,
};

const rosterSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.75,
  mt: 1,
};

const playerSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.6,
  maxWidth: "100%",
};

const nickSx = {
  fontSize: "0.75rem",
  color: colors.text.secondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "8rem",
};

const moreSx = {
  fontSize: "0.75rem",
  color: colors.text.muted,
  alignSelf: "center",
};

const emptySx = {
  fontSize: "0.85rem",
  color: colors.text.secondary,
  mt: 0.5,
};

const actionsSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mt: 1.25,
  flexWrap: "wrap",
};

const reserveSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.8125rem",
  px: 1.75,
  py: 0.7,
  borderRadius: "999px",
  backgroundColor: colors.primary.main,
  color: "#0B0D0F",
  "&:hover": { backgroundColor: colors.primary.hover },
};

const linkSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: colors.primary.main,
  textDecoration: "none",
  "&:hover": { color: colors.primary.hover },
};

export default function NextMatchNight({ preferredRegion }) {
  const { nights, preferredRegion: apiPreferredRegion, loading, basePath, refetch } = useMatchNights();
  const track = useTemplateTrack();
  const [selectedNight, setSelectedNight] = React.useState(null);
  const [selectedRegion, setSelectedRegion] = React.useState(() =>
    normalizeMatchNightRegion(preferredRegion)
  );
  const hasAppliedPreferredRegion = React.useRef(false);
  const night = React.useMemo(
    () => nights.find((item) => item.region === selectedRegion),
    [nights, selectedRegion]
  );
  const scheduledAt = night?.scheduledAt ? new Date(night.scheduledAt) : null;
  const countdown = useCountdown(scheduledAt);

  React.useEffect(() => {
    if (hasAppliedPreferredRegion.current) return;
    const explicit = MATCH_NIGHT_REGIONS.includes(preferredRegion)
      ? preferredRegion
      : MATCH_NIGHT_REGIONS.includes(apiPreferredRegion)
        ? apiPreferredRegion
        : null;
    if (!explicit) return;
    setSelectedRegion(explicit);
    hasAppliedPreferredRegion.current = true;
  }, [preferredRegion, apiPreferredRegion]);

  if (loading) return null;

  if (!night || !scheduledAt || Number.isNaN(scheduledAt.getTime())) {
    return (
      <Box sx={wrapSx}>
        <Typography sx={kickerSx}>Next match night</Typography>
        <Box role="tablist" aria-label="Match night region" sx={tabRowSx}>
          {MATCH_NIGHT_REGIONS.map((region) => (
            <Box
              key={region}
              component="button"
              type="button"
              role="tab"
              aria-selected={selectedRegion === region}
              onClick={() => setSelectedRegion(region)}
              sx={tabSx(selectedRegion === region)}
            >
              {region}
            </Box>
          ))}
        </Box>
        <Typography sx={emptySx}>
          No {selectedRegion} night is open to book right now. Join Discord and we will post the next
          one there.
        </Typography>
        <Box component={Link} to="/match-night" sx={{ ...linkSx, display: "inline-block", mt: 1 }}>
          Match nights →
        </Box>
      </Box>
    );
  }

  const confirmedCount = night.confirmedCount || 0;
  const status = formatMatchNightStatus({
    confirmedCount,
    minToFire: night.minToFire,
    isToday: isNightScheduledToday(scheduledAt, night.region),
  });
  const players = Array.isArray(night.confirmedPlayers) ? night.confirmedPlayers : [];
  const extra = Math.max(0, confirmedCount - players.length);

  return (
    <Box sx={wrapSx}>
      <Typography sx={kickerSx}>Next match night</Typography>
      <Box role="tablist" aria-label="Match night region" sx={tabRowSx}>
        {MATCH_NIGHT_REGIONS.map((region) => (
          <Box
            key={region}
            component="button"
            type="button"
            role="tab"
            aria-selected={selectedRegion === region}
            onClick={() => setSelectedRegion(region)}
            sx={tabSx(selectedRegion === region)}
          >
            {region}
          </Box>
        ))}
      </Box>
      <Typography sx={dateSx}>{formatNightDateLabel(scheduledAt, night.region)}</Typography>
      <Typography sx={metaSx}>
        {countdown.label}
        {night.region ? ` · ${formatServersLine(night.region, night.serverLocations)}` : ""}
      </Typography>
      <Typography sx={countSx}>
        {status ? status.label : `${confirmedCount} confirmed`}
      </Typography>
      {status?.note ? <Typography sx={noteSx}>{status.note}</Typography> : null}
      {players.length > 0 ? (
        <Box sx={rosterSx}>
          {players.map((player, index) => (
            <Box key={`${player.nickname}-${index}`} sx={playerSx}>
              <Avatar
                src={player.picture || undefined}
                alt=""
                sx={{ width: 28, height: 28, fontSize: "0.75rem" }}
              >
                {(player.nickname || "P").slice(0, 1)}
              </Avatar>
              <Typography sx={nickSx}>{player.nickname}</Typography>
            </Box>
          ))}
          {extra > 0 ? <Typography sx={moreSx}>+{extra} more</Typography> : null}
        </Box>
      ) : (
        <Typography sx={emptySx}>No one has reserved a spot yet.</Typography>
      )}
      <Box sx={actionsSx}>
        <Button
          sx={reserveSx}
          onClick={() => {
            track("match_night_reserve_clicked", { surface: "hero" });
            setSelectedNight(night);
          }}
        >
          Reserve your spot
        </Button>
        <Box component={Link} to="/match-night" sx={linkSx}>
          All match nights →
        </Box>
      </Box>
      <MatchNightRsvpDialog
        open={Boolean(selectedNight)}
        night={selectedNight}
        basePath={basePath}
        onClose={() => setSelectedNight(null)}
        onRsvpSuccess={refetch}
      />
    </Box>
  );
}

NextMatchNight.propTypes = {
  preferredRegion: PropTypes.oneOf(MATCH_NIGHT_REGIONS),
};
